import { useCallback, useEffect, useState } from 'react'
import { createLogger } from '@/lib/logger'
import { subscriptionSeatsApi } from '@/modules/subscription'
import { useOrganizationStore } from '@/store/organization.store'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  invalidateSubscriptionCache,
  useOrganizationSubscription,
} from './useOrganizationSubscription'

export type { SeatPreview } from '@/modules/subscription'

const logger = createLogger('hooks/useSubscriptionSeats')
const usageKey = (organizationId: string | undefined) =>
  ['subscription-seats', 'usage', organizationId] as const
const historyKey = (organizationId: string | undefined) =>
  ['subscription-seats', 'history', organizationId] as const
const previewKey = (
  organizationId: string | undefined,
  revision: string,
  seats: number | null,
) => ['subscription-seats', 'preview', organizationId, revision, seats] as const

export function useSubscriptionSeats() {
  const { currentOrganization } = useOrganizationStore()
  const organizationId = currentOrganization?.id
  const { subscription } = useOrganizationSubscription(organizationId)
  const queryClient = useQueryClient()
  const [previewQuantity, setPreviewQuantity] = useState<number | null>(null)
  const [historyLimit, setHistoryLimit] = useState<number | null>(null)
  const revision = JSON.stringify([
    subscription?.id,
    subscription?.seats,
    subscription?.currentPeriodEnd,
    subscription?.metadata?.billingInterval,
  ])

  const usage = useQuery({
    queryKey: usageKey(organizationId),
    queryFn: () => subscriptionSeatsApi.usage(organizationId!),
    enabled: Boolean(organizationId),
  })
  const history = useQuery({
    queryKey: [...historyKey(organizationId), historyLimit],
    queryFn: () => subscriptionSeatsApi.history(organizationId!, historyLimit!),
    enabled: Boolean(organizationId) && historyLimit !== null,
  })
  const preview = useQuery({
    queryKey: previewKey(organizationId, revision, previewQuantity),
    queryFn: () =>
      subscriptionSeatsApi.preview(organizationId!, previewQuantity!),
    enabled:
      Boolean(organizationId) &&
      previewQuantity !== null &&
      previewQuantity !== subscription?.seats,
    retry: false,
    staleTime: 0,
  })
  const clearPreview = useCallback(() => setPreviewQuantity(null), [])
  useEffect(() => {
    setPreviewQuantity(null)
    setHistoryLimit(null)
  }, [organizationId, revision])

  const refreshUsage = useCallback(
    async (id: string) => {
      await queryClient.invalidateQueries({ queryKey: usageKey(id) })
    },
    [queryClient],
  )
  const update = useMutation({
    mutationFn: ({
      id,
      seats,
      immediate,
    }: {
      id: string
      seats: number
      immediate: boolean
    }) => subscriptionSeatsApi.update(id, seats, immediate),
    // SeatManagement toasts the API's message; the global toast doubled it.
    meta: { silent: true },
    onSuccess: async (_data, { id }) => {
      invalidateSubscriptionCache(id)
      await Promise.all([
        refreshUsage(id),
        queryClient.invalidateQueries({ queryKey: historyKey(id) }),
      ])
      setPreviewQuantity(null)
    },
    onSettled: (_data, _error, { id }) =>
      queryClient.invalidateQueries({ queryKey: ['billing-operation', id] }),
  })
  const assignment = useMutation({
    mutationFn: ({
      id,
      userId,
      active,
    }: {
      id: string
      userId: string
      active: boolean
    }) => subscriptionSeatsApi.assign(id, userId, active),
    onSuccess: (data, { id }) => queryClient.setQueryData(usageKey(id), data),
  })
  // Callers own the error: the sync that runs when Billing or Members opens
  // is background housekeeping and stays quiet, and a manual sync toasts its
  // own message. The global mutation toast said "Something went wrong" over
  // the page on every open while seats couldn't sync.
  const recalculation = useMutation({
    mutationFn: (id: string) => subscriptionSeatsApi.recalculate(id),
    meta: { silent: true },
    onSuccess: async (_data, id) => {
      await refreshUsage(id)
    },
  })
  const requireOrganization = useCallback(() => {
    if (!organizationId) throw new Error('No organization selected')
    return organizationId
  }, [organizationId])
  const fetchSeatUsage = useCallback(async () => {
    if (!organizationId) return
    await queryClient.fetchQuery({
      queryKey: usageKey(organizationId),
      queryFn: () => subscriptionSeatsApi.usage(organizationId),
      staleTime: 0,
    })
  }, [organizationId, queryClient])
  const fetchSeatHistory = useCallback(
    async (limit = 50) => {
      const id = requireOrganization()
      setHistoryLimit(limit)
      await queryClient.fetchQuery({
        queryKey: [...historyKey(id), limit],
        queryFn: () => subscriptionSeatsApi.history(id, limit),
        staleTime: 0,
      })
    },
    [requireOrganization, queryClient],
  )
  const previewSeatChange = useCallback(
    async (seats: number) => {
      if (
        !organizationId ||
        !Number.isSafeInteger(seats) ||
        seats < 1 ||
        seats === subscription?.seats
      ) {
        setPreviewQuantity(null)
        return null
      }
      setPreviewQuantity(seats)
      try {
        return await queryClient.fetchQuery({
          queryKey: previewKey(organizationId, revision, seats),
          queryFn: () => subscriptionSeatsApi.preview(organizationId, seats),
          staleTime: 0,
          retry: false,
        })
      } catch (error) {
        logger.error('Failed to preview seat change:', error)
        return null
      }
    },
    [organizationId, subscription?.seats, revision, queryClient],
  )
  const updateSeats = useCallback(
    async (seats: number, immediate = true) => {
      if (!subscription) throw new Error('No active subscription')
      await update.mutateAsync({ id: requireOrganization(), seats, immediate })
    },
    [subscription, requireOrganization, update.mutateAsync],
  )
  const assignSeat = useCallback(
    async (userId: string) => {
      await assignment.mutateAsync({
        id: requireOrganization(),
        userId,
        active: true,
      })
    },
    [requireOrganization, assignment.mutateAsync],
  )
  const unassignSeat = useCallback(
    async (userId: string) => {
      await assignment.mutateAsync({
        id: requireOrganization(),
        userId,
        active: false,
      })
    },
    [requireOrganization, assignment.mutateAsync],
  )
  const recalculateSeatAssignments = useCallback(
    () => recalculation.mutateAsync(requireOrganization()),
    [requireOrganization, recalculation.mutateAsync],
  )
  const seatUsage = usage.data ?? null
  const error =
    update.error ??
    assignment.error ??
    recalculation.error ??
    usage.error ??
    history.error
  return {
    subscription,
    seatUsage,
    seatHistory: history.data ?? [],
    seatPreview:
      previewQuantity !== null && !preview.error
        ? (preview.data ?? null)
        : null,
    isLoading: usage.isLoading || history.isFetching,
    isUpdating:
      update.isPending || assignment.isPending || recalculation.isPending,
    isPreviewing: preview.isFetching,
    error: error instanceof Error ? error : null,
    // Seat usage failed with nothing cached: the card shows an error with a
    // retry instead of a skeleton that never resolves.
    isUsageError: usage.isError && !usage.data,
    retryUsage: () => void usage.refetch(),
    updateSeats,
    previewSeatChange,
    clearPreview,
    assignSeat,
    unassignSeat,
    fetchSeatUsage,
    fetchSeatHistory,
    recalculateSeatAssignments,
    canReduceSeats:
      subscription?.status === 'trialing' ||
      (subscription?.metadata?.billingInterval !== 'year' &&
        (subscription?.allowSeatReduction ?? true)),
    availableSeats: seatUsage?.availableSeats ?? 0,
    usedSeats: seatUsage?.usedSeats ?? 0,
    seatUtilization: seatUsage?.totalSeats
      ? (seatUsage.usedSeats / seatUsage.totalSeats) * 100
      : 0,
  }
}
